// Profile: who you are signed in as, what plan the workspace is on, what it has
// used, and the state of any upgrade you have asked for.
//
// The plan half is deliberately here rather than only on /plans — "what am I
// paying for and what happened to my request" is an account question, and it is
// the first place someone looks after clicking their own name.

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import { currentWorkspace, listConnections } from "../lib/api";
import { formatLimit, formatMoney, loadEntitlements } from "../lib/entitlements";
import {
  cancelPlanChangeRequest, isOpen, listPaymentMethods, listPlanChangeRequests,
  STATUS_LABEL, type PaymentMethod, type PlanChangeRequest,
} from "../lib/billing";
import { renameWorkspace } from "../lib/workspace";
import { useWorkspace } from "../lib/WorkspaceContext";
import { AccountShapeCard } from "../components/AccountShapeCard";
import { WorkspaceKindCard } from "../components/WorkspaceKindCard";
import { CalendarSettings } from "../components/CalendarSettings";
import { PersonalDetailsCard } from "../components/PersonalDetailsCard";
import { TimeZoneCard } from "../components/TimeZoneCard";
import { logError } from "../lib/logger";
import type { Connection, Entitlements } from "../lib/types";

export function Profile({ session }: { session: Session }) {
  const { can, refresh } = useWorkspace();
  const [workspaceName, setWorkspaceName] = useState<string | null>(null);
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  // Renaming is inline rather than a dialog: it is one field, and the name is
  // already on screen next to it.
  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);
  const [ent, setEnt] = useState<Entitlements | null>(null);
  const [requests, setRequests] = useState<PlanChangeRequest[]>([]);
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [connections, setConnections] = useState<Connection[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const ws = await currentWorkspace();

    setWorkspaceId(ws);
    const { data: row } = await supabase
      .from("workspaces").select("name").eq("id", ws).maybeSingle();
    setWorkspaceName(row?.name ?? null);

    setEnt(await loadEntitlements(ws).catch(() => null));
    setRequests(await listPlanChangeRequests(ws).catch(() => []));
    setConnections(await listConnections(ws).catch(() => []));
    setMethods(await listPaymentMethods().catch(() => []));
  }, []);

  useEffect(() => {
    load().catch((e) => {
      setError(e.message ?? String(e));
      void logError("app", e, { step: "load_profile" });
    });
  }, [load]);

  async function cancel(id: string) {
    setBusy(true);
    try {
      await cancelPlanChangeRequest(id);
      await load();
    } catch (e: any) {
      setError(e.message ?? String(e));
    } finally {
      setBusy(false);
    }
  }

  async function saveName() {
    if (!workspaceId) return;
    setRenameBusy(true);
    setError(null);
    try {
      setWorkspaceName(await renameWorkspace(workspaceId, draftName));
      setRenaming(false);
      // The name is in the switcher and in half the sentences on screen, so the
      // whole context reloads rather than this one field.
      await refresh();
    } catch (e: any) {
      setError(e.message ?? String(e));
      void logError("app", e, { step: "rename_workspace", workspaceId });
    } finally {
      setRenameBusy(false);
    }
  }

  const open = requests.find(isOpen) ?? null;
  const closed = requests.filter((r) => !isOpen(r));
  const metered = Object.entries(ent?.features ?? {})
    .filter(([, f]) => f.kind === "quota" && f.implemented);

  return (
    <>
      <h1>Profile</h1>
      {error && <div className="card err">{error}</div>}

      <div className="card">
        <h2>Account</h2>
        <dl className="kv">
          <dt>Signed in as</dt>
          <dd>{session.user.email}</dd>
          <dt>Workspace</dt>
          <dd>
            {renaming ? (
              <span className="row rename-row">
                <label className="visually-hidden" htmlFor="workspace-name">Workspace name</label>
                <input id="workspace-name" value={draftName} maxLength={120} autoFocus
                       onChange={(e) => setDraftName(e.target.value)}
                       onKeyDown={(e) => {
                         if (e.key === "Enter" && draftName.trim()) void saveName();
                         if (e.key === "Escape") setRenaming(false);
                       }} />
                <button className="sm primary" disabled={renameBusy || !draftName.trim()}
                        onClick={() => void saveName()}>
                  {renameBusy ? "Saving…" : "Save"}
                </button>
                <button className="sm" disabled={renameBusy}
                        onClick={() => setRenaming(false)}>Cancel</button>
              </span>
            ) : (
              <>
                {workspaceName ?? "—"}
                {/* `workspace.manage` — the same permission the database
                    checks. An Admin may rename; a Publisher may not. */}
                {can("workspace.manage") && workspaceName && (
                  <>
                    {" "}
                    <button className="link" onClick={() => {
                      setDraftName(workspaceName);
                      setRenaming(true);
                    }}>
                      Rename
                    </button>
                  </>
                )}
              </>
            )}
          </dd>
          <dt>Connected accounts</dt>
          <dd>
            {connections.filter((c) => c.status !== "revoked").length}{" "}
            <Link to="/connections">manage</Link>
          </dd>
          <dt>Member since</dt>
          <dd>{new Date(session.user.created_at).toLocaleDateString()}</dd>
        </dl>
        <div className="row" style={{ marginTop: ".8rem" }}>
          <button className="sm" onClick={() => supabase.auth.signOut()}>Sign out</button>
          <Link className="btn-link" to="/data-deletion#account">Delete account</Link>
        </div>
      </div>

      {/* Who support is talking to, and where an invoice would go. */}
      <PersonalDetailsCard />

      {/* Which clock your times mean. Above the calendar settings because it
          changes what every date on the next card reads as. */}
      <TimeZoneCard />

      <AccountShapeCard />

      <WorkspaceKindCard />

      {/* Which calendars you see, and which of your devices see this one. Both
          belong to you rather than to the workspace, which is why they are here
          and not on the calendar screen's settings. */}
      <CalendarSettings workspaceId={workspaceId} />

      <div className="card">
        <div className="row">
          <h2 style={{ flex: 1, margin: 0 }}>Plan</h2>
          <Link className="btn-link primary" to="/plans">
            {ent?.plan?.code === "free" ? "Upgrade" : "Change plan"}
          </Link>
        </div>

        <p className="plan-line">
          <strong>{ent?.plan?.name ?? "Free"}</strong>
          {ent?.subscription?.current_period_end && (
            <span className="muted">
              {" "}· renews {new Date(ent.subscription.current_period_end).toLocaleDateString()}
            </span>
          )}
          {ent?.subscription?.status && ent.subscription.status !== "active" && (
            <span className="chip warn" style={{ marginLeft: ".5rem" }}>
              {ent.subscription.status}
            </span>
          )}
        </p>

        {metered.length > 0 && (
          <table className="usage">
            <tbody>
              {metered.map(([key, f]) => {
                const pct = f.limit === null ? 0
                  : f.limit === 0 ? 100
                  : Math.min(100, Math.round(((f.used ?? 0) / f.limit) * 100));
                return (
                  <tr key={key}>
                    <th scope="row">{f.name}</th>
                    <td style={{ width: "40%" }}>
                      {f.limit === null
                        ? <span className="muted">Unlimited</span>
                        : <div className="bar"><div style={{ width: `${pct}%` }} /></div>}
                    </td>
                    <td className="muted">
                      {formatLimit(f.used ?? 0, f.unit)} of {formatLimit(f.limit, f.unit)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {open && (
        <div className="card">
          <h2>Your upgrade request</h2>
          <p className="request-head">
            <strong>{open.from_plan} → {open.to_plan}</strong>
            {open.amount_minor != null && open.currency && (
              <> · {formatMoney(open.amount_minor, open.currency)}{" "}
                {open.billing_interval === "lifetime" ? "one-time" : `per ${open.billing_interval}`}</>
            )}
          </p>
          <p><span className="chip warn">{STATUS_LABEL[open.status]}</span></p>

          {/* What happens next, in the customer's terms. Nobody should have to
              ask support what "pending" means. */}
          {(() => {
            const m = methods.find((x) => x.code === open.payment_method);
            return m?.instructions
              ? <p className="notice">{m.name}: {m.instructions}</p>
              : <p className="notice">We will email {open.contact_email ?? "you"} with what to do next.</p>;
          })()}

          {open.note && <p className="muted">Your note: {open.note}</p>}
          <p className="muted">Asked {new Date(open.created_at).toLocaleString()}</p>

          <button className="sm" disabled={busy} onClick={() => cancel(open.id)}>
            Cancel this request
          </button>
        </div>
      )}

      {closed.length > 0 && (
        <div className="card">
          <h2>Billing history</h2>
          <table className="usage">
            <tbody>
              {closed.map((r) => (
                <tr key={r.id}>
                  <th scope="row">{new Date(r.created_at).toLocaleDateString()}</th>
                  <td>{r.from_plan} → {r.to_plan}</td>
                  <td className="muted">{STATUS_LABEL[r.status]}</td>
                  <td className="muted">{r.review_note ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
